import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Upload,
  Search,
  Image,
  FileText,
  Loader2,
  Calendar,
  Eye,
  Download,
  Trash2,
  Mail,
  Package,
  Sparkles,
  Link2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  File,
  LinkIcon,
  Unlink,
  ArrowUpDown,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Flag,
  X,
  GripVertical,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCw,
  Layers,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import SwipeMatchPage from "./SwipeMatchPage";

// Constants for panel width persistence
const PANEL_WIDTH_KEY = "bankadmin_inbox_panel_width";
const DEFAULT_PANEL_WIDTH = 50;
const MIN_PANEL_WIDTH = 30;
const MAX_PANEL_WIDTH = 70;

// Constants for filter persistence
const INBOX_FILTERS_KEY = "bankadmin_inbox_filters";

// Upload constraints (shared by the picker and drag-and-drop)
const VALID_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;

const InboxPage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const scrollAreaRef = useRef(null);

  // Load saved filters from localStorage
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(INBOX_FILTERS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const savedFilters = getSavedFilters();

  // Core state
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [linkedFiles, setLinkedFiles] = useState([]);
  const [unlinkedFiles, setUnlinkedFiles] = useState([]);
  const [flaggedFiles, setFlaggedFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // Files marked as "no_transaction"
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);

  // Filter states - initialize from localStorage
  const [search, setSearch] = useState(savedFilters.search || "");
  const [activeTab, setActiveTab] = useState(savedFilters.activeTab || "unlinked");
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || "upload_date");
  const [sortOrder, setSortOrder] = useState(savedFilters.sortOrder || "desc");
  const [filterMonth, setFilterMonth] = useState(savedFilters.filterMonth || "all");
  const [filterDateType, setFilterDateType] = useState(savedFilters.filterDateType || "received");
  const [showFilters, setShowFilters] = useState(false);

  // Feature flags
  const [filterNonFinancial, setFilterNonFinancial] = useState(true);

  // Resizable panel
  const [detailPanelWidth, setDetailPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_KEY);
    return saved ? parseFloat(saved) : DEFAULT_PANEL_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);

  // Batch download state
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [swipeOpen, setSwipeOpen] = useState(false);
  const [batchStartDate, setBatchStartDate] = useState("");
  const [batchEndDate, setBatchEndDate] = useState("");
  const [onlyUnexported, setOnlyUnexported] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedPeriodType, setSelectedPeriodType] = useState("quarter");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarters, setSelectedQuarters] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      search,
      activeTab,
      sortBy,
      sortOrder,
      filterMonth,
      filterDateType,
    };
    localStorage.setItem(INBOX_FILTERS_KEY, JSON.stringify(filters));
  }, [search, activeTab, sortBy, sortOrder, filterMonth, filterDateType]);

  // Check if any filters are active
  const hasActiveFilters = search || (filterMonth && filterMonth !== "all") ||
    sortBy !== "upload_date" || sortOrder !== "desc" || filterDateType !== "received";

  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setFilterMonth("all");
    setSortBy("upload_date");
    setSortOrder("desc");
    setFilterDateType("received");
    localStorage.removeItem(INBOX_FILTERS_KEY);
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;
      const clampedWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth));
      setDetailPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem(PANEL_WIDTH_KEY, String(detailPanelWidth));
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, detailPanelWidth]);

  // Fetch feature flag on mount
  useEffect(() => {
    const fetchFeatureFlag = async () => {
      try {
        const res = await apiService.getFeatureFlag('filter_non_financial_documents');
        setFilterNonFinancial(res.data.enabled);
      } catch (error) {
        console.error("Failed to fetch feature flag:", error);
        setFilterNonFinancial(true);
      }
    };
    fetchFeatureFlag();
  }, []);

  // Generate month options for filter
  const getMonthOptions = () => {
    const options = [{ value: "all", label: "All Months" }];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = getMonthOptions();

  const fetchFiles = async () => {
    try {
      const res = await apiService.getFiles({
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      let allFiles = res.data;

      // Filter out non-financial documents only if the feature flag is enabled
      if (filterNonFinancial) {
        allFiles = allFiles.filter(file => {
          const analysis = file.analysis;
          if (!analysis || !analysis.document_info) return true;
          const docInfo = analysis.document_info;
          return docInfo.is_financial_document !== false;
        });
      }

      // Filter by month based on selected date type
      if (filterMonth && filterMonth !== "all") {
        allFiles = allFiles.filter(file => {
          let dateToUse;
          if (filterDateType === "invoice") {
            dateToUse = file.analysis?.document_info?.date || file.upload_date;
          } else {
            dateToUse = file.upload_date;
          }
          if (!dateToUse) return false;
          const fileMonth = dateToUse.substring(0, 7);
          return fileMonth === filterMonth;
        });
      }

      setFiles(allFiles);

      // Separate linked, unlinked, flagged, and pending (no_transaction) files
      const linked = [];
      const unlinked = [];
      const flagged = [];
      const pending = [];

      for (const file of allFiles) {
        if (file.file_status === "no_transaction") {
          pending.push(file);
        } else if (file.is_flagged) {
          flagged.push(file);
        } else if (file.linked_transaction_count > 0 || file.is_linked) {
          linked.push(file);
        } else {
          unlinked.push(file);
        }
      }

      setLinkedFiles(linked);
      setUnlinkedFiles(unlinked);
      setFlaggedFiles(flagged);
      setPendingFiles(pending);

      // Auto-select first file if none selected
      if (!selectedFile && allFiles.length > 0) {
        const firstFile = activeTab === "linked" ? linked[0] : 
          activeTab === "flagged" ? flagged[0] : 
          activeTab === "pending" ? pending[0] : unlinked[0];
        if (firstFile) setSelectedFile(firstFile);
      }
    } catch (error) {
      console.error("Failed to fetch files:", error);
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchFiles();
  }, [sortBy, sortOrder, filterNonFinancial, filterMonth, filterDateType]);

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchFiles();
  };

  // Shared upload path for both the picker and drag-and-drop, so selecting
  // multiple files works identically either way. Validates each file, uploads
  // the valid ones sequentially with live progress, and reports what was
  // skipped or failed so nothing drops silently.
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const valid = [];
    const rejected = [];
    for (const file of files) {
      if (!VALID_UPLOAD_TYPES.includes(file.type)) {
        rejected.push(`${file.name} (unsupported type)`);
      } else if (file.size > MAX_UPLOAD_SIZE) {
        rejected.push(`${file.name} (over 10MB)`);
      } else {
        valid.push(file);
      }
    }

    if (rejected.length > 0) {
      toast.error(
        rejected.length === 1
          ? `Skipped ${rejected[0]}`
          : `Skipped ${rejected.length} files: images and PDFs up to 10MB only`
      );
    }
    if (valid.length === 0) return;

    setUploading(true);
    const multiple = valid.length > 1;
    const toastId = multiple ? toast.loading(`Uploading 0/${valid.length}...`) : undefined;
    let success = 0;
    const failed = [];

    for (const file of valid) {
      try {
        await apiService.uploadFile(file);
        success++;
        if (toastId) toast.loading(`Uploading ${success}/${valid.length}...`, { id: toastId });
      } catch (error) {
        failed.push(file.name);
      }
    }

    setUploading(false);

    if (success > 0) {
      const msg = multiple ? `${success} file${success > 1 ? "s" : ""} uploaded!` : `"${valid[0].name}" uploaded!`;
      if (toastId) toast.success(msg, { id: toastId });
      else toast.success(msg);
      setActiveTab("unlinked");
      await fetchFiles();
    } else if (toastId) {
      toast.dismiss(toastId);
    }

    if (failed.length > 0) {
      toast.error(
        failed.length === 1 ? `Failed to upload "${failed[0]}"` : `Failed to upload ${failed.length} files`
      );
    }
  };

  const handleFileSelect = async (e) => {
    await uploadFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current && !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    await uploadFiles(e.dataTransfer.files);
  };

  const handleRowClick = (file) => {
    setSelectedFile(file);
  };

  const handleDeleteFile = async (file, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${file.filename}"?`)) return;

    setDeleting(file.id);
    try {
      await apiService.deleteFile(file.id);
      toast.success("File deleted");
      if (selectedFile?.id === file.id) setSelectedFile(null);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete file");
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleFlag = async (file, e) => {
    if (e) e.stopPropagation();
    try {
      await apiService.toggleFileFlag(file.id);
      toast.success(file.is_flagged ? "File unflagged" : "File flagged");
      fetchFiles();
    } catch (error) {
      toast.error("Failed to toggle flag");
    }
  };

  const handleReanalyzeUnlinked = async () => {
    setReanalyzing(true);
    try {
      const res = await apiService.reanalyzeUnlinkedFiles();
      toast.success(`Reanalyzed ${res.data.analyzed} of ${res.data.total} files`);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reanalyze files");
    } finally {
      setReanalyzing(false);
    }
  };

  // Period calculation helpers for batch download
  const getQuarterDates = (quarter, year) => {
    const quarters = {
      1: { start: `${year}-01-01`, end: `${year}-03-31` },
      2: { start: `${year}-04-01`, end: `${year}-06-30` },
      3: { start: `${year}-07-01`, end: `${year}-09-30` },
      4: { start: `${year}-10-01`, end: `${year}-12-31` },
    };
    return quarters[quarter];
  };

  const getMonthDates = (month, year) => {
    const lastDay = new Date(year, month, 0).getDate();
    const monthStr = String(month).padStart(2, '0');
    return { start: `${year}-${monthStr}-01`, end: `${year}-${monthStr}-${lastDay}` };
  };

  const calculateSelectedDates = () => {
    if (selectedPeriodType === "custom") return { start: batchStartDate, end: batchEndDate };
    if (selectedPeriodType === "year") return { start: `${selectedYear}-01-01`, end: `${selectedYear}-12-31` };
    if (selectedPeriodType === "quarter" && selectedQuarters.length > 0) {
      const sortedQ = [...selectedQuarters].sort((a, b) => a - b);
      const firstQ = getQuarterDates(sortedQ[0], selectedYear);
      const lastQ = getQuarterDates(sortedQ[sortedQ.length - 1], selectedYear);
      return { start: firstQ.start, end: lastQ.end };
    }
    if (selectedPeriodType === "month" && selectedMonths.length > 0) {
      const sortedM = [...selectedMonths].sort((a, b) => a - b);
      const firstM = getMonthDates(sortedM[0], selectedYear);
      const lastM = getMonthDates(sortedM[sortedM.length - 1], selectedYear);
      return { start: firstM.start, end: lastM.end };
    }
    return { start: null, end: null };
  };

  const toggleQuarter = (q) => setSelectedQuarters(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);
  const toggleMonth = (m) => setSelectedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  const handleBatchDownload = async () => {
    const dates = calculateSelectedDates();
    if (!dates.start || !dates.end) {
      toast.error("Please select a period");
      return;
    }

    setDownloading(true);
    try {
      const response = await apiService.downloadBatchReceipts(dates.start, dates.end, onlyUnexported);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipts_${dates.start}_to_${dates.end}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
      setBatchDialogOpen(false);
      fetchFiles();
    } catch (error) {
      // The response is a blob on error, so read the JSON detail out of it.
      let detail = "Failed to download receipts";
      if (error.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await error.response.data.text()).detail || detail;
        } catch (_) {}
      } else {
        detail = error.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return <Image className="w-5 h-5 text-purple-600" />;
    return <FileText className="w-5 h-5 text-blue-600" />;
  };

  const getFileIconBg = (contentType) => {
    if (contentType?.startsWith("image/")) return "bg-purple-100";
    return "bg-blue-100";
  };

  const getCurrentFileList = () => {
    if (activeTab === "linked") return linkedFiles;
    if (activeTab === "flagged") return flaggedFiles;
    if (activeTab === "pending") return pendingFiles;
    if (activeTab === "all") return files;
    return unlinkedFiles;
  };

  const currentFiles = getCurrentFileList();

  // After linking a document we stay in the inbox and move to the next one to
  // review, rather than jumping to the transactions screen. The just-linked
  // file drops out of the unlinked list on refresh, so selecting its list
  // neighbour keeps the reviewer on an unhandled document.
  const selectNextFile = (currentFileId) => {
    const list = getCurrentFileList();
    const idx = list.findIndex((f) => f.id === currentFileId);
    if (idx === -1) return;
    const next = list[idx + 1] || list[idx - 1] || null;
    setSelectedFile(next);
  };

  return (
    <div
      ref={dropZoneRef}
      className={`h-[calc(100vh-4rem)] flex flex-col relative ${isDragging ? 'ring-2 ring-emerald-500 ring-inset' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      data-testid="inbox-page"
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-emerald-50/90 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl border-2 border-dashed border-emerald-500">
          <div className="text-center">
            <Upload className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-bounce" />
            <p className="text-xl font-semibold text-emerald-700">Drop files here</p>
            <p className="text-emerald-600 mt-1">Images and PDFs up to 10MB</p>
          </div>
        </div>
      )}

      {/* Compact Header */}
      <div className="flex-shrink-0 px-3 sm:px-4 py-2 border-b bg-white">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search files..."
                className="pl-8 h-8 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Filter Toggle */}
          <Button
            variant={hasActiveFilters ? "default" : "outline"}
            size="sm"
            className="h-8 flex-shrink-0"
            onClick={() => setShowFilters(!showFilters)}
          >
            <ChevronDown className={`w-4 h-4 sm:mr-1 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            <span className="hidden sm:inline">Filters</span>
          </Button>

          {/* Quick Stats */}
          <span className="text-sm text-slate-500 hidden md:inline flex-shrink-0">
            {files.length} files
          </span>

          {/* Actions */}
          {["admin", "manager", "controller"].includes(user?.role) && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              onClick={() => setSwipeOpen(true)}
              title="Swipe through confident receipt matches"
            >
              <Layers className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Start Swiping</span>
            </Button>
          )}

          <Button variant="outline" size="sm" className="h-8" onClick={() => setBatchDialogOpen(true)}>
            <Package className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Export</span>
          </Button>

          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setLoading(true); fetchFiles(); }} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>

          <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple onChange={handleFileSelect} className="hidden" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-8 bg-emerald-600 hover:bg-emerald-700">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 sm:mr-1" />}
            <span className="hidden sm:inline">Upload</span>
          </Button>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="mt-2 pt-2 border-t flex flex-wrap items-center gap-2">
            <Select value={filterDateType} onValueChange={setFilterDateType}>
              <SelectTrigger className="h-7 text-xs w-[100px]">
                <SelectValue placeholder="Date type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="invoice">Invoice</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="h-7 text-xs w-[120px]">
                <Calendar className="w-3 h-3 mr-1" />
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-7 text-xs w-[110px]">
                <ArrowUpDown className="w-3 h-3 mr-1" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upload_date">Upload Date</SelectItem>
                <SelectItem value="invoice_date">Invoice Date</SelectItem>
                <SelectItem value="filename">Filename</SelectItem>
                <SelectItem value="size">Size</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="h-7 text-xs w-[90px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest</SelectItem>
                <SelectItem value="asc">Oldest</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearFilters}>
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-2 pt-2 border-t flex items-center justify-between">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-8 p-1">
              <TabsTrigger value="unlinked" className="text-xs px-2 h-6">
                <Unlink className="w-3 h-3 mr-1" />
                Unlinked ({unlinkedFiles.length})
              </TabsTrigger>
              <TabsTrigger value="pending" className="text-xs px-2 h-6">
                <AlertCircle className="w-3 h-3 mr-1 text-amber-500" />
                Pending ({pendingFiles.length})
              </TabsTrigger>
              <TabsTrigger value="linked" className="text-xs px-2 h-6">
                <LinkIcon className="w-3 h-3 mr-1" />
                Linked ({linkedFiles.length})
              </TabsTrigger>
              <TabsTrigger value="flagged" className="text-xs px-2 h-6">
                <Flag className="w-3 h-3 mr-1 text-orange-500" />
                Flagged ({flaggedFiles.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs px-2 h-6">
                <File className="w-3 h-3 mr-1" />
                All ({files.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {activeTab === "unlinked" && unlinkedFiles.length > 0 && (user?.role === "admin" || user?.role === "manager") && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleReanalyzeUnlinked} disabled={reanalyzing}>
              {reanalyzing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Reanalyze All
            </Button>
          )}
        </div>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">
        {/* Left Panel - File List */}
        <div
          className="flex flex-col bg-white lg:border-r"
          style={{ width: isMobile ? '100%' : `${100 - detailPanelWidth}%` }}
        >
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : currentFiles.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                {activeTab === "linked" ? <LinkIcon className="w-8 h-8 text-slate-400" /> : <Unlink className="w-8 h-8 text-slate-400" />}
              </div>
              <p className="text-sm text-slate-500">
                {activeTab === "linked" ? "No linked files yet" : activeTab === "flagged" ? "No flagged files" : "No unlinked files"}
              </p>
            </div>
          ) : (
            <>
              {/* List Header */}
              <div className="flex-shrink-0 flex items-center gap-3 px-3 py-1.5 border-b bg-slate-50 text-xs text-slate-500">
                <span className="flex-1">File</span>
                <span className="w-24 text-right hidden sm:block">Amount</span>
                <span className="w-20 text-right hidden sm:block">Date</span>
              </div>

              {/* Scrollable List */}
              <ScrollArea className="flex-1" viewportRef={scrollAreaRef}>
                <TooltipProvider>
                  {currentFiles.map((file) => {
                    const isSelected = selectedFile?.id === file.id;
                    const invoiceDate = file.analysis?.document_info?.date;
                    const amount = file.analysis?.document_info?.amount;
                    const currency = file.analysis?.document_info?.currency || '€';
                    const merchant = file.analysis?.document_info?.merchant_name;

                    return (
                      <div
                        key={file.id}
                        onClick={() => handleRowClick(file)}
                        className={`flex items-center gap-3 px-3 py-2.5 border-b cursor-pointer transition-colors ${
                          isSelected && !isMobile ? "bg-emerald-50 border-l-2 border-l-emerald-500" :
                          file.is_flagged ? "bg-orange-50/50 hover:bg-orange-50" : "hover:bg-slate-50"
                        }`}
                        data-testid={`file-row-${file.id}`}
                      >
                        {/* File Icon */}
                        <div className={`w-8 h-8 rounded-lg ${getFileIconBg(file.content_type)} flex items-center justify-center flex-shrink-0`}>
                          {getFileIcon(file.content_type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                              {file.filename}
                            </span>
                            {file.is_flagged && <Flag className="w-3 h-3 text-orange-500 fill-orange-200" />}
                            {file.file_status === "no_transaction" && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="h-4 px-1 text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                    <AlertCircle className="w-2.5 h-2.5" />
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent><p>Pending import - will reanalyze after next import</p></TooltipContent>
                              </Tooltip>
                            )}
                            {file.analysis && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="h-4 px-1 text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                                    <Sparkles className="w-2.5 h-2.5" />
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent><p>AI Analyzed</p></TooltipContent>
                              </Tooltip>
                            )}
                            {file.is_linked && (
                              <Badge variant="outline" className="h-4 px-1 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                <LinkIcon className="w-2.5 h-2.5 mr-0.5" />
                                {file.linked_transaction_count}
                              </Badge>
                            )}
                            {file.exported_at && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="h-4 px-1 text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                    <CheckCircle2 className="w-2.5 h-2.5" />
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent><p>Exported {formatDate(file.exported_at)}</p></TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-400">
                              {merchant || formatSize(file.size)}
                            </span>
                            {file.source === "email" && (
                              <Badge variant="secondary" className="h-4 px-1 text-[10px] bg-blue-50 text-blue-700">
                                <Mail className="w-2.5 h-2.5" />
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="w-24 text-right hidden sm:block">
                          {amount ? (
                            <span className="text-sm font-medium text-emerald-600">{currency}{amount}</span>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </div>

                        {/* Date */}
                        <div className="w-20 text-right hidden sm:block">
                          <span className="text-xs text-slate-500">
                            {invoiceDate ? formatDate(invoiceDate) : formatDate(file.upload_date)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </TooltipProvider>
              </ScrollArea>

              {/* Footer */}
              <div className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 border-t bg-slate-50 text-xs text-slate-500">
                <span>Showing {currentFiles.length} files</span>
              </div>
            </>
          )}
        </div>

        {/* Resize Handle */}
        <div
          className="hidden lg:flex w-1 bg-slate-200 hover:bg-emerald-400 cursor-col-resize items-center justify-center transition-colors group"
          onMouseDown={handleMouseDown}
        >
          <div className="w-4 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-3 h-3 text-slate-500" />
          </div>
        </div>

        {/* Right Panel - File Detail */}
        <div
          className="hidden lg:flex flex-col bg-slate-50"
          style={{ width: `${detailPanelWidth}%` }}
        >
          {selectedFile ? (
            <FileDetailPanel
              file={selectedFile}
              onRefresh={fetchFiles}
              onDelete={handleDeleteFile}
              onToggleFlag={handleToggleFlag}
              onAdvanceToNext={selectNextFile}
              formatDate={formatDate}
              formatSize={formatSize}
              getFileIcon={getFileIcon}
              getFileIconBg={getFileIconBg}
              navigate={navigate}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <p>Select a file to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Batch Download Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Export Receipts</DialogTitle>
            <DialogDescription>Download linked receipts for a specific period as a ZIP file.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Year Selector */}
            <div className="flex items-center justify-between">
              <Label>Year</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedYear(y => y - 1)} className="h-8 w-8 p-0">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-lg font-semibold w-16 text-center">{selectedYear}</span>
                <Button variant="outline" size="sm" onClick={() => setSelectedYear(y => y + 1)} disabled={selectedYear >= new Date().getFullYear()} className="h-8 w-8 p-0">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Period Type Tabs */}
            <Tabs value={selectedPeriodType} onValueChange={(v) => { setSelectedPeriodType(v); setSelectedQuarters([]); setSelectedMonths([]); }}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="quarter">Quarters</TabsTrigger>
                <TabsTrigger value="month">Months</TabsTrigger>
                <TabsTrigger value="year">Full Year</TabsTrigger>
                <TabsTrigger value="custom">Custom</TabsTrigger>
              </TabsList>
            </Tabs>

            {selectedPeriodType === "quarter" && (
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((q) => (
                  <Button
                    key={q}
                    variant={selectedQuarters.includes(q) ? "default" : "outline"}
                    className={`h-12 ${selectedQuarters.includes(q) ? 'bg-emerald-600' : ''}`}
                    onClick={() => toggleQuarter(q)}
                  >
                    Q{q}
                  </Button>
                ))}
              </div>
            )}

            {selectedPeriodType === "month" && (
              <div className="grid grid-cols-4 gap-2">
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                  <Button
                    key={i}
                    variant={selectedMonths.includes(i + 1) ? "default" : "outline"}
                    size="sm"
                    className={selectedMonths.includes(i + 1) ? 'bg-emerald-600' : ''}
                    onClick={() => toggleMonth(i + 1)}
                  >
                    {m}
                  </Button>
                ))}
              </div>
            )}

            {selectedPeriodType === "year" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <p className="font-medium text-emerald-800">Full Year {selectedYear}</p>
              </div>
            )}

            {selectedPeriodType === "custom" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={batchStartDate} onChange={(e) => setBatchStartDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={batchEndDate} onChange={(e) => setBatchEndDate(e.target.value)} />
                </div>
              </div>
            )}

            <label className="flex items-start gap-2 cursor-pointer rounded-lg border p-3 bg-slate-50">
              <Checkbox
                checked={onlyUnexported}
                onCheckedChange={(v) => setOnlyUnexported(v === true)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium text-slate-900">Only receipts not yet exported</span>
                <span className="block text-xs text-slate-500">Skip anything already included in a previous export for this period.</span>
              </span>
            </label>

            <Button onClick={handleBatchDownload} disabled={downloading || !calculateSelectedDates().start} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download ZIP
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {swipeOpen && (
        <SwipeMatchPage
          onClose={() => {
            setSwipeOpen(false);
            setLoading(true);
            fetchFiles();
          }}
        />
      )}
    </div>
  );
};

// File Detail Side Panel Component
const FileDetailPanel = ({ file, onRefresh, onDelete, onToggleFlag, onAdvanceToNext, formatDate, formatSize, getFileIcon, getFileIconBg, navigate }) => {
  const [fileData, setFileData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [attachingSuggestion, setAttachingSuggestion] = useState(null);
  const [linkedTransactions, setLinkedTransactions] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  
  // Zoom and fullscreen state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [settingStatus, setSettingStatus] = useState(false);
  const [unpacking, setUnpacking] = useState(false);

  const isEmail =
    file.content_type === "message/rfc822" ||
    (file.filename || "").toLowerCase().endsWith(".eml");

  // Reset zoom/rotation when file changes
  useEffect(() => {
    setZoomLevel(1);
    setRotation(0);
    setIsFullscreen(false);
  }, [file.id]);

  // Fetch file data (for preview) when file changes
  useEffect(() => {
    const fetchFileData = async () => {
      setLoadingData(true);
      try {
        const res = await apiService.getFile(file.id);
        setFileData(res.data);
      } catch (error) {
        console.error("Failed to load file data:", error);
      } finally {
        setLoadingData(false);
      }
    };

    const fetchSuggestions = async () => {
      setLoadingSuggestions(true);
      try {
        const res = await apiService.getFileSuggestions(file.id);
        setSuggestions(res.data.suggestions || []);
      } catch (error) {
        console.error("Failed to fetch suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const fetchLinkedTransactions = async () => {
      if (file.is_linked || file.linked_transaction_count > 0) {
        setLoadingLinked(true);
        try {
          const res = await apiService.getFileLinkedTransactions(file.id);
          setLinkedTransactions(res.data.transactions || []);
        } catch (error) {
          console.error("Failed to fetch linked transactions:", error);
          setLinkedTransactions([]);
        } finally {
          setLoadingLinked(false);
        }
      } else {
        setLinkedTransactions([]);
      }
    };

    fetchFileData();
    fetchSuggestions();
    fetchLinkedTransactions();
  }, [file.id, file.is_linked, file.linked_transaction_count]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await apiService.analyzeFile(file.id);
      toast.success("Analysis complete");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleLinkToTransaction = async (transactionId) => {
    setAttachingSuggestion(transactionId);
    try {
      await apiService.attachFile(transactionId, file.id);
      toast.success("File linked to transaction!");
      onAdvanceToNext?.(file.id);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link file");
    } finally {
      setAttachingSuggestion(null);
    }
  };

  const handleUnpack = async () => {
    setUnpacking(true);
    try {
      const res = await apiService.unpackEmail(file.id);
      const count = res.data?.unpacked ?? 0;
      if (count > 0) {
        toast.success(`Unpacked ${count} attachment${count === 1 ? "" : "s"} from email`);
      } else {
        toast.info("No attachments found inside this email");
      }
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unpack email");
    } finally {
      setUnpacking(false);
    }
  };

  const handleSetNoTransaction = async () => {
    setSettingStatus(true);
    try {
      const newStatus = file.file_status === "no_transaction" ? "active" : "no_transaction";
      await apiService.setFileStatus(file.id, newStatus);
      toast.success(newStatus === "no_transaction" ? "Marked as pending import" : "Moved back to unlinked");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setSettingStatus(false);
    }
  };

  const docInfo = file.analysis?.document_info;

  return (
    <ScrollArea className="flex-1">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-12 h-12 rounded-xl ${getFileIconBg(file.content_type)} flex items-center justify-center flex-shrink-0`}>
              {getFileIcon(file.content_type)}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 truncate">{file.filename}</h2>
              <p className="text-sm text-slate-500">{formatSize(file.size)} • {formatDate(file.upload_date)}</p>
              {file.exported_at && (
                <Badge variant="outline" className="mt-1 h-5 px-1.5 text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Exported {formatDate(file.exported_at)}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={file.is_flagged ? "default" : "outline"}
            size="sm"
            className={file.is_flagged ? "bg-orange-500 hover:bg-orange-600" : ""}
            onClick={(e) => onToggleFlag(file, e)}
          >
            <Flag className={`w-4 h-4 ${file.is_flagged ? "" : "mr-1"}`} />
            {!file.is_flagged && "Flag"}
          </Button>

          <Button variant="outline" size="sm" onClick={handleAnalyze} disabled={analyzing}>
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {file.analysis ? "Re-analyze" : "Analyze"}
          </Button>

          {isEmail && (
            <Button variant="outline" size="sm" onClick={handleUnpack} disabled={unpacking}>
              {unpacking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
              Unpack email
            </Button>
          )}

          {/* Mark as "Transaction not imported" - only show for unlinked files */}
          {!file.is_linked && file.linked_transaction_count === 0 && (
            <Button
              variant={file.file_status === "no_transaction" ? "default" : "outline"}
              size="sm"
              className={file.file_status === "no_transaction" ? "bg-amber-500 hover:bg-amber-600" : "text-amber-600 hover:text-amber-700"}
              onClick={handleSetNoTransaction}
              disabled={settingStatus}
              title={file.file_status === "no_transaction" ? "Move back to unlinked" : "Mark as pending import - will be re-analyzed after next import"}
            >
              {settingStatus ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {file.file_status === "no_transaction" ? "Pending" : "No Txn"}
                </>
              )}
            </Button>
          )}

          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={(e) => onDelete(file, e)}>
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
        </div>

        {/* File Preview with Zoom & Fullscreen */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-2 border-b bg-slate-50 flex items-center justify-between">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" />
              Preview
            </h3>
            {fileData?.content_type?.startsWith("image/") && fileData?.data && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                  disabled={zoomLevel <= 0.5}
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs text-slate-500 w-10 text-center">{Math.round(zoomLevel * 100)}%</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                  disabled={zoomLevel >= 3}
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setRotation((rotation + 90) % 360)}
                  title="Rotate"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsFullscreen(true)}
                  title="Fullscreen"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
            {fileData?.content_type === "application/pdf" && fileData?.data && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setIsFullscreen(true)}
                title="Fullscreen"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <div className="p-2">
            {loadingData ? (
              <div className="h-48 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : fileData?.content_type?.startsWith("image/") && fileData?.data ? (
              <div className="bg-slate-100 rounded-lg flex items-center justify-center overflow-auto max-h-[300px]">
                <img
                  src={`data:${fileData.content_type};base64,${fileData.data}`}
                  alt={file.filename}
                  className="object-contain transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                    maxHeight: zoomLevel <= 1 ? '280px' : 'none'
                  }}
                />
              </div>
            ) : fileData?.content_type === "application/pdf" && fileData?.data ? (
              <div className="bg-slate-100 rounded-lg overflow-hidden">
                <iframe
                  src={`data:application/pdf;base64,${fileData.data}`}
                  title={file.filename}
                  className="w-full h-[200px]"
                />
                <div className="p-2 border-t bg-white flex justify-center">
                  <a href={`data:application/pdf;base64,${fileData.data}`} download={file.filename}>
                    <Button variant="outline" size="sm">
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </a>
                </div>
              </div>
            ) : (
              <div className="h-32 flex flex-col items-center justify-center text-slate-400">
                <FileText className="w-10 h-10 mb-2" />
                <p className="text-sm">Preview not available</p>
              </div>
            )}
          </div>
        </div>

        {/* Fullscreen Preview Dialog */}
        <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 overflow-hidden">
            <div className="flex flex-col h-full">
              {/* Fullscreen Header */}
              <div className="flex-shrink-0 p-3 border-b bg-slate-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${getFileIconBg(file.content_type)} flex items-center justify-center`}>
                    {getFileIcon(file.content_type)}
                  </div>
                  <span className="text-white font-medium">{file.filename}</span>
                </div>
                <div className="flex items-center gap-2">
                  {fileData?.content_type?.startsWith("image/") && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-slate-800"
                        onClick={() => setZoomLevel(Math.max(0.25, zoomLevel - 0.25))}
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <span className="text-white text-sm w-14 text-center">{Math.round(zoomLevel * 100)}%</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-slate-800"
                        onClick={() => setZoomLevel(Math.min(5, zoomLevel + 0.25))}
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-slate-800"
                        onClick={() => setRotation((rotation + 90) % 360)}
                      >
                        <RotateCw className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-slate-800"
                        onClick={() => { setZoomLevel(1); setRotation(0); }}
                      >
                        Reset
                      </Button>
                    </>
                  )}
                  <a href={fileData?.data ? `data:${fileData.content_type};base64,${fileData.data}` : '#'} download={file.filename}>
                    <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-slate-800"
                    onClick={() => setIsFullscreen(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Fullscreen Content */}
              <div className="flex-1 overflow-auto bg-slate-950 flex items-center justify-center p-4">
                {fileData?.content_type?.startsWith("image/") && fileData?.data ? (
                  <img
                    src={`data:${fileData.content_type};base64,${fileData.data}`}
                    alt={file.filename}
                    className="transition-transform duration-200"
                    style={{ 
                      transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                      maxWidth: zoomLevel <= 1 ? '100%' : 'none',
                      maxHeight: zoomLevel <= 1 ? '100%' : 'none'
                    }}
                  />
                ) : fileData?.content_type === "application/pdf" && fileData?.data ? (
                  <iframe
                    src={`data:application/pdf;base64,${fileData.data}`}
                    title={file.filename}
                    className="w-full h-full bg-white"
                  />
                ) : (
                  <div className="text-slate-500 text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Preview not available</p>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Analysis Results */}
        {file.analysis && docInfo && (
          <div className="bg-purple-50 rounded-lg border border-purple-200 overflow-hidden">
            <div className="p-2 border-b border-purple-200 bg-purple-100/50">
              <h3 className="text-xs font-medium text-purple-800 uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                AI Analysis
              </h3>
            </div>
            <div className="p-3 space-y-2">
              {docInfo.merchant_name && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Merchant</span>
                  <span className="text-sm font-medium text-slate-900">{docInfo.merchant_name}</span>
                </div>
              )}
              {docInfo.amount && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Amount</span>
                  <span className="text-sm font-bold text-emerald-600">{docInfo.currency || '€'}{docInfo.amount}</span>
                </div>
              )}
              {docInfo.date && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Invoice Date</span>
                  <span className="text-sm font-medium text-slate-900">{docInfo.date}</span>
                </div>
              )}
              {docInfo.reference && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Reference</span>
                  <span className="text-sm font-mono text-slate-700">{docInfo.reference}</span>
                </div>
              )}
              {docInfo.document_type && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Type</span>
                  <Badge variant="outline" className="text-xs capitalize">{docInfo.document_type}</Badge>
                </div>
              )}
              {docInfo.is_financial_document === false && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  This does not appear to be a financial document
                </div>
              )}
            </div>
          </div>
        )}

        {/* Linked Transactions */}
        {(file.is_linked || file.linked_transaction_count > 0) && (
          <div className="bg-emerald-50 rounded-lg border border-emerald-200 overflow-hidden">
            <div className="p-2 border-b border-emerald-200 bg-emerald-100/50">
              <h3 className="text-xs font-medium text-emerald-800 uppercase tracking-wider flex items-center gap-1.5">
                <LinkIcon className="w-3.5 h-3.5" />
                Linked Transactions ({linkedTransactions.length})
              </h3>
            </div>
            <div className="p-2">
              {loadingLinked ? (
                <div className="py-4 text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto" />
                </div>
              ) : linkedTransactions.length > 0 ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {linkedTransactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="p-2 bg-white rounded border hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/transactions?tx=${tx.id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${
                            tx.direction === "income" ? "bg-emerald-100" : "bg-red-100"
                          }`}>
                            {tx.direction === "income" ? (
                              <TrendingUp className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-3 h-3 text-red-500" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900 truncate max-w-[150px]">
                              {tx.counterparty_name || "Transaction"}
                            </p>
                            <p className="text-xs text-slate-500">{formatDate(tx.transaction_date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${
                            tx.direction === "income" ? "text-emerald-600" : "text-red-500"
                          }`}>
                            {tx.direction === "income" ? "+" : "-"}{tx.amount?.toFixed(2)} {tx.currency}
                          </span>
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-emerald-600 text-center py-2">No linked transactions</p>
              )}
            </div>
          </div>
        )}

        {/* Suggested Transactions (for unlinked files) */}
        {!file.is_linked && file.linked_transaction_count === 0 && (
          <div className="bg-slate-50 rounded-lg border overflow-hidden">
            <div className="p-2 border-b bg-slate-100">
              <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5" />
                Suggested Matches
              </h3>
            </div>
            <div className="p-2">
              {loadingSuggestions ? (
                <div className="py-4 text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
                  <p className="text-xs text-slate-400 mt-1">Finding matches...</p>
                </div>
              ) : suggestions.length > 0 ? (
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.transaction_id}
                      className={`p-2 bg-white rounded border transition-colors ${
                        index === 0
                          ? "border-emerald-300 ring-1 ring-emerald-200"
                          : "hover:border-emerald-300"
                      }`}
                    >
                      {index === 0 && (
                        <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-600 mb-1">
                          Best match
                        </p>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                            suggestion.direction === "income" ? "bg-emerald-100" : "bg-red-100"
                          }`}>
                            {suggestion.direction === "income" ? (
                              <TrendingUp className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-3 h-3 text-red-500" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {suggestion.counterparty_name || "Transaction"}
                            </p>
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <span>{formatDate(suggestion.transaction_date)}</span>
                              <span>·</span>
                              <span className={suggestion.direction === "income" ? "text-emerald-600" : "text-red-500"}>
                                {suggestion.amount?.toFixed(2)} {suggestion.currency}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                            {suggestion.score}%
                          </Badge>
                          <Button
                            size="sm"
                            className="h-6 text-xs bg-emerald-600 hover:bg-emerald-700 px-2"
                            onClick={() => handleLinkToTransaction(suggestion.transaction_id)}
                            disabled={attachingSuggestion === suggestion.transaction_id}
                          >
                            {attachingSuggestion === suggestion.transaction_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <LinkIcon className="w-3 h-3" />
                                {index === 0 && <span className="ml-1">Accept</span>}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      {/* Match Reasons */}
                      {suggestion.reasons && suggestion.reasons.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-slate-100">
                          {suggestion.reasons.map((reason, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="h-4 px-1.5 text-[9px] bg-slate-50 text-slate-600 border-slate-200"
                            >
                              {reason}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 text-center py-4">
                  {file.analysis ? "No matching transactions found" : "Analyze file to find matches"}
                </p>
              )}
            </div>
          </div>
        )}

        {/* File Info */}
        <div className="bg-white rounded-lg border p-3 space-y-2 text-sm">
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider">File Info</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="text-slate-500">Source</div>
            <div className="text-slate-900 flex items-center gap-1">
              {file.source === "email" ? (
                <>
                  <Mail className="w-3 h-3 text-blue-500" />
                  Email
                </>
              ) : (
                "Upload"
              )}
            </div>
            <div className="text-slate-500">Type</div>
            <div className="text-slate-900">{file.content_type}</div>
            <div className="text-slate-500">Uploaded</div>
            <div className="text-slate-900">{formatDate(file.upload_date)}</div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export default InboxPage;
